import { useState, useEffect } from 'react'
import { IconBox, IconBackpack, IconLock, IconPlus, IconArrowRight } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import { gearItemsApi, kitCollectionsApi, containersApi, type GearItem } from '../../lib/api.ts'
import { GearItemForm } from '../gear/GearItemForm.tsx'
import { KitCollectionForm } from '../kit-collections/KitCollectionForm.tsx'

type StatCardProps = { label: string; value: string | number; icon: React.ReactNode; to: string; loading?: boolean }

function StatCard({ label, value, icon, to, loading }: StatCardProps) {
  const navigate = useNavigate()
  return (
    <button
      onClick={() => navigate(to)}
      className="flex items-center gap-4 p-5 rounded-xl bg-surface-overlay border border-border-default hover:border-border-strong hover:bg-surface-raised transition-all text-left group"
    >
      <div className="w-11 h-11 rounded-lg bg-surface-raised border border-border-default flex items-center justify-center text-zinc-400 group-hover:text-brand-orange group-hover:border-brand-orange/30 transition-colors shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-3xl font-semibold text-zinc-100">
          {loading ? <span className="text-zinc-600 text-xl">—</span> : value}
        </div>
        <div className="text-sm text-zinc-400 mt-0.5">{label}</div>
      </div>
      <IconArrowRight size={16} className="text-zinc-600 group-hover:text-zinc-300 transition-colors shrink-0" />
    </button>
  )
}

export function LockerRoomPage() {
  const navigate = useNavigate()
  const [counts, setCounts] = useState({ lockers: 0, gear: 0, bags: 0 })
  const [loading, setLoading] = useState(true)
  const [allGear, setAllGear] = useState<GearItem[]>([])
  const [viewItem, setViewItem] = useState<GearItem | null>(null)
  const [viewOpen, setViewOpen] = useState(false)
  const [kitCollectionFormOpen, setKitCollectionFormOpen] = useState(false)
  const [recentGear, setRecentGear] = useState<{
    id: string
    title: string
    brand?: string | null
    size?: string | null
    condition?: string | null
    colors: string[]
    customizations: string[]
  }[]>([])

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const [lockersRes, gearRes, bagsRes] = await Promise.allSettled([
          containersApi.list({ type: 'locker' }),
          gearItemsApi.list(),
          kitCollectionsApi.list(),
        ])

        const lockers = lockersRes.status === 'fulfilled' ? lockersRes.value.data ?? [] : []
        const gear = gearRes.status === 'fulfilled' ? gearRes.value.data ?? [] : []
        const bags = bagsRes.status === 'fulfilled' ? bagsRes.value.data ?? [] : []

        setCounts({ lockers: lockers.length, gear: gear.length, bags: bags.length })
        setAllGear(gear)
        // Most recent 3 items
        setRecentGear(gear.slice(-3).reverse().map((item: any) => ({
          id: item.id,
          title: item.title,
          brand: item.brand ?? null,
          size: (item as any).sizeDisplayLabel ?? item.size ?? null,
          condition: item.attributes?.find((a: any) => a.attributeType === 'condition')?.value?.displayLabel ?? null,
          colors: item.attributes?.filter((a: any) => a.attributeType === 'color').map((a: any) => a.value?.displayLabel ?? '') ?? [],
          customizations: item.attributes?.filter((a: any) => a.attributeType === 'additive').map((a: any) => a.value?.displayLabel ?? '') ?? [],
        })))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  return (
    <div className="min-h-full p-4 sm:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-zinc-100 mb-2">Locker Room</h1>
          <p className="text-sm sm:text-base text-zinc-400">Welcome back. Here's your collection at a glance.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 sm:mb-10">
          <StatCard label="Lockers" value={counts.lockers} icon={<IconLock size={20} />} to="/lockers" loading={loading} />
          <StatCard label="Kit Collections" value={counts.bags} icon={<IconBackpack size={20} />} to="/kit-collections" loading={loading} />
          <StatCard label="Gear Items" value={counts.gear} icon={<IconBox size={20} />} to="/gear" loading={loading} />
        </div>

        {/* Quick Actions */}
        <div className="mb-10">
          <h2 className="text-xs font-medium uppercase tracking-widest text-zinc-400 mb-3">Quick Actions</h2>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => navigate('/lockers?action=new')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-brand-orange text-white text-sm font-medium hover:bg-brand-orange-dark transition-colors"
            >
              <IconPlus size={16} /> Add Locker
            </button>
            <button
              onClick={() => navigate('/gear?action=new')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-brand-orange text-white text-sm font-medium hover:bg-brand-orange-dark transition-colors"
            >
              <IconPlus size={16} /> Add Gear
            </button>
            <button
              onClick={() => setKitCollectionFormOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-brand-orange text-white text-sm font-medium hover:bg-brand-orange-dark transition-colors"
            >
              <IconPlus size={16} /> Add Kit Collection
            </button>
          </div>
        </div>

        {/* Recent gear */}
        <div>
          <h2 className="text-xs font-medium uppercase tracking-widest text-zinc-400 mb-3">Recent gear</h2>
          {loading ? (
            <div className="rounded-xl border border-border-default bg-surface-overlay p-8 text-center text-sm text-zinc-500">
              Loading...
            </div>
          ) : recentGear.length === 0 ? (
            <div className="rounded-xl border border-border-default bg-surface-overlay p-16 text-center">
              <div className="w-14 h-14 rounded-xl bg-surface-raised border border-border-default flex items-center justify-center mx-auto mb-4">
                <IconBox size={22} className="text-zinc-600" />
              </div>
              <p className="text-base text-zinc-400 mb-3">Your locker is empty</p>
              <button
                onClick={() => navigate('/gear?action=new')}
                className="text-sm text-brand-orange hover:text-brand-orange-light transition-colors"
              >
                Add your first piece of gear →
              </button>
            </div>
          ) : (
            <div className="rounded-xl border border-border-default bg-surface-overlay overflow-hidden">
              {recentGear.map((item, i) => (
                <div
                  key={item.id}
                  onClick={() => {
                    const full = allGear.find(g => g.id === item.id)
                    if (full) { setViewItem(full); setViewOpen(true) }
                  }}
                  className={`flex items-center gap-4 px-5 py-3.5 cursor-pointer hover:bg-surface-raised transition-colors ${i > 0 ? 'border-t border-border-subtle' : ''}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-surface-raised border border-border-default flex items-center justify-center shrink-0">
                    <IconBox size={14} className="text-zinc-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-200 truncate">{item.title}</div>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                      {[item.brand, item.size].filter(Boolean).length > 0 && (
                        <span className="text-xs text-zinc-500">
                          {[item.brand, item.size].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      {item.condition && (
                        <span className="text-xs text-zinc-600">{item.condition}</span>
                      )}
                      {item.colors.length > 0 && (
                        <span className="text-xs text-zinc-600">{item.colors.slice(0, 3).join(', ')}</span>
                      )}
                      {item.customizations.length > 0 && (
                        <span className="text-xs text-zinc-600 italic">{item.customizations.slice(0, 2).join(', ')}</span>
                      )}
                    </div>
                  </div>
                  <IconArrowRight size={13} className="text-zinc-600 shrink-0" />
                </div>
              ))}
              <div
                onClick={() => navigate('/gear')}
                className="flex items-center justify-center px-5 py-3 border-t border-border-subtle cursor-pointer hover:bg-surface-raised transition-colors"
              >
                <span className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors">View all gear →</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <KitCollectionForm
        open={kitCollectionFormOpen}
        onClose={() => setKitCollectionFormOpen(false)}
        onSaved={() => { setKitCollectionFormOpen(false); setCounts(prev => ({ ...prev, bags: prev.bags + 1 })) }}
        mode="new"
      />

      {viewItem && (
        <GearItemForm
          mode="view"
          item={viewItem}
          open={viewOpen}
          onClose={() => { setViewOpen(false); setViewItem(null) }}
          onModeChange={() => {}}
        />
      )}
    </div>
  )
}
